import React, { useMemo } from 'react';
import { ColumnsType } from 'antd/es/table';
import { DataTable, Row, Text } from '@ydistri/ds';
import { formatDate } from '@ydistri/generic-utils';
import dayjs from 'dayjs';
import { ExecutionType, PickingProgressStatus } from '@ydistri/api-sdk';
import { computeRemSize } from '@ydistri/theme';
import { translateExecutionType } from '../../evaluationsLib';
import { InfoIconTooltip } from '../../../../../components/icons/InfoIconTooltip';
import CalculationInfoDataSourceTooltip from './CalculationInfoDataSourceTooltip';

interface CalculationInfoProps {
  createdAt?: string;
  finishExecutionUntil?: string | null;
  status?: PickingProgressStatus;
  executionType?: ExecutionType | null;
  loading: boolean;
}

interface OverallStatistics {
  title: string;
  key: string;
  value: string | number;
  fromNow?: string | number;
  tooltip?: React.ReactNode;
}

const NOT_AVAILABLE_LABEL = 'Not Available';

const overallEvaluationsColumns: ColumnsType<OverallStatistics> = [
  {
    title: 'Title',
    dataIndex: 'title',
    key: 'title',
    width: computeRemSize(150),
    render: (value: string, record) => {
      if (record.tooltip) {
        return (
          <Row $alignItems="center" $gap="0.25rem">
            <Text>{value}</Text>
            <InfoIconTooltip content={record.tooltip} />
          </Row>
        );
      } else {
        return value;
      }
    },
  },
  {
    title: 'Value',
    dataIndex: 'value',
    key: 'value',
  },
  {
    title: 'From now',
    dataIndex: 'fromNow',
    key: 'fromNow',
    align: 'left',
    render: value => (value ? <Text $italic={true}>({value})</Text> : null),
  },
];

/**
 * Displays basic information about the calculation, such as creation date
 * and finish execution date.
 */
const CalculationInfo: React.FC<CalculationInfoProps> = ({
  createdAt,
  finishExecutionUntil,
  status,
  executionType,
  loading,
}) => {
  const tableData: OverallStatistics[] = useMemo(() => {
    return [
      {
        title: 'Status',
        key: 'status',
        value: status ? status.valueOf() : NOT_AVAILABLE_LABEL,
      },
      {
        title: 'Data source',
        key: 'dataSource',
        value: executionType ? translateExecutionType(executionType) : NOT_AVAILABLE_LABEL,
        tooltip: <CalculationInfoDataSourceTooltip />,
      },
      {
        title: 'Created at:',
        key: 'calculationCreatedAt',
        value: createdAt ? formatDate(createdAt) : NOT_AVAILABLE_LABEL,
        fromNow: createdAt ? dayjs(createdAt).fromNow() : '',
      },
      {
        title: 'Finish execution until:',
        key: 'finishExecutionUntil',
        value: finishExecutionUntil ? formatDate(finishExecutionUntil) : NOT_AVAILABLE_LABEL,
        fromNow: finishExecutionUntil ? dayjs(finishExecutionUntil).fromNow() : '',
      },
    ];
  }, [createdAt, executionType, finishExecutionUntil, status]);

  return (
    <DataTable<OverallStatistics>
      id="calculation-info"
      rowKey="key"
      dataSource={tableData}
      columns={overallEvaluationsColumns}
      loading={loading}
    />
  );
};

export default CalculationInfo;
